import { useQuery, useMutation } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import type { ApiResponse } from '@/lib/api/types'

/**
 * 프리미엄 시설 간단 방문 통계 (지난 30일)
 *
 * 관련 기획: docs/plan/facility-premium-v2.md 섹션 6.5
 * API: GET /api/v1/facility-manager/stats/simple
 */
export interface FacilitySimpleStats {
  /** 기간 식별자 (예: 'LAST_30_DAYS') */
  period: string
  /** 시설 페이지 조회수 */
  pageViews: number
  /** 전화 연결 클릭수 */
  phoneClicks: number
  /** 상담 문의 접수수 */
  consultations: number
}

/**
 * 로그인한 시설관리자의 간단 방문 통계 조회 (프리미엄 전용)
 * - staleTime 5분: 자주 업데이트 불필요
 * - retry 1회: 권한/플랜 미보유 시 빠르게 실패
 */
export function useFacilitySimpleStats(enabled = true) {
  return useQuery({
    queryKey: ['facility-manager', 'stats', 'simple'],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<FacilitySimpleStats>>(
        '/facility-manager/stats/simple'
      )
      return res.data.data
    },
    staleTime: 5 * 60 * 1000,
    enabled,
    retry: 1,
  })
}

// ─── 상세 통계 (프리미엄 전용) ───

export interface DailyVisitStat {
  date: string
  pageViews: number
  phoneClicks: number
}

export interface DailyCountStat {
  date: string
  count: number
}

export interface SummaryWithComparison {
  pageViews: number
  phoneClicks: number
  consultations: number
  prevPageViews: number
  prevPhoneClicks: number
  prevConsultations: number
}

export interface ReviewAnalysis {
  totalCount: number
  avgRating: number
  unrepliedCount: number
  ratingDistribution: number[]
}

export interface Benchmark {
  regionName: string
  facilityCount: number
  avgBookmarks: number
  myBookmarks: number
  avgReviewRating: number
  myReviewRating: number
  avgPageViews30d: number
  myPageViews30d: number
}

export interface DetailedStats {
  visitTrend: DailyVisitStat[]
  consultationTrend: DailyCountStat[]
  summary: SummaryWithComparison
  reviewStats: ReviewAnalysis
  benchmark: Benchmark
}

export function useFacilityDetailedStats(days = 30, enabled = true) {
  return useQuery({
    queryKey: ['facility-manager', 'stats', 'detailed', days],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<DetailedStats>>(
        '/facility-manager/stats/detailed',
        { params: { days } }
      )
      return res.data.data
    },
    staleTime: 5 * 60 * 1000,
    enabled,
    retry: 1,
  })
}

/**
 * 시설 방문 이벤트 로깅 (비동기 Fire-and-forget)
 *
 * TODO(Phase C 백엔드): `POST /api/v1/facilities/{id}/visit-event` API가 아직 구현되지 않았을 수 있음.
 *   백엔드 가용 시 body schema 확정 후 실 연동. 현재는 서버가 404를 돌려줘도 조용히 무시한다.
 *
 * 실패해도 UX에 영향 없어야 하므로 어떤 에러도 throw하지 않는다.
 */
export type VisitEventType = 'PAGE_VIEW' | 'PHONE_CLICK'

export async function logFacilityVisitEvent(
  facilityId: number,
  eventType: VisitEventType,
): Promise<void> {
  try {
    await apiClient.post(`/facilities/${facilityId}/visit-event`, { eventType })
  } catch {
    // 방문 이벤트 로깅 실패는 UX에 영향 없어야 하므로 무시
  }
}

/**
 * 방문 이벤트 로깅 뮤테이션 훅 (React Query)
 * - onError 없이 silent fail
 */
export function useLogVisitEvent() {
  return useMutation({
    mutationFn: async ({ facilityId, eventType }: { facilityId: number; eventType: VisitEventType }) => {
      await logFacilityVisitEvent(facilityId, eventType)
    },
  })
}
